import { useEffect, useRef, useState } from "react";
import { useQuery } from "@apollo/client";
import { GET_THEME_EXTENSION_STATUS } from "../graphql/Queries/ThemeExtensions";
import { buildAppBlockDeepLink, buildAppEmbedDeepLink } from "../utils/ShopifyAdminLinkTools";

// Theme app blocks, with where the theme editor deep link places each one
const APP_BLOCKS = [
  { handle: "countdown_timer_block", name: "Countdown timer", target: "mainSection", template: "product" },
  { handle: "announcement_bar_block", name: "Announcement bar", target: "sectionGroup:header", template: "index" },
  { handle: "cart_total_discounts", name: "Cart savings", target: "mainSection", template: "cart" },
  { handle: "hero_banner_block", name: "Hero banner", target: "mainSection", template: "index", requiresHeroBanner: true },
];

/**
 * App embed and app block status for one theme: the live theme until the merchant picks another.
 * Refetches when the window regains focus, since the merchant changes the theme in another tab.
 */
export default function useThemeExtensionStatus({ storeUrl = "", appEmbedUuid = "" }) {
  const [selectedThemeId, setSelectedThemeId] = useState(null);
  const hasInitializedTheme = useRef(false);
  const { data, refetch } = useQuery(GET_THEME_EXTENSION_STATUS(selectedThemeId), {
    fetchPolicy: "cache-and-network",
    errorPolicy: "all"
  });

  // API returns PascalCase
  const status = data?.themeExtensionStatus || {};
  const themes = status.Themes || [];

  useEffect(() => {
    const liveTheme = themes.find(t => t.Role === "main");
    if (liveTheme && !hasInitializedTheme.current) {
      hasInitializedTheme.current = true;
      setSelectedThemeId(liveTheme.Id);
    }
  }, [themes]);

  useEffect(() => {
    const onFocus = () => refetch();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refetch]);

  const blocks = APP_BLOCKS
    .filter(block => !block.requiresHeroBanner || status.HeroBannerEnabled)
    .map(block => ({
      handle: block.handle,
      name: block.name,
      enabled: !!status.AppBlocks?.find(b => b.Handle === block.handle)?.Enabled,
      deepLink: buildAppBlockDeepLink(storeUrl, appEmbedUuid, block.handle, block.target, block.template, selectedThemeId),
    }));

  return {
    themeOptions: themes.map(theme => ({
      label: theme.Role === "main" ? `${theme.Name} (Live)` : theme.Name,
      value: theme.Id
    })),
    selectedThemeId,
    selectTheme: setSelectedThemeId,
    // Undefined until the status has loaded
    appEmbedEnabled: status.AppEmbedEnabled,
    appEmbedDeepLink: buildAppEmbedDeepLink(storeUrl, appEmbedUuid, "app_embed", null, selectedThemeId),
    supportsAppBlocks: status.IsOnlineStore2 ?? true,
    blocks,
    isBlockEnabled: (handle) => blocks.some(block => block.handle === handle && block.enabled),
    blockDeepLink: (handle) => blocks.find(block => block.handle === handle)?.deepLink,
  };
}
